import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rm } from "@/lib/format";
import { useDashboardCtx } from "@/context/DashboardContext";
import { Loader2, ArrowRight } from "lucide-react";

export default function Attribution() {
  const { customerId } = useDashboardCtx();
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState(null);

  useEffect(() => {
    const params = customerId ? { customer_id: customerId } : {};
    api.get("/dashboard/overview", { params }).then((r) => setOverview(r.data)).catch(() => {});
    api.get("/product-profitability", { params }).then((r) => setProducts(r.data)).catch(() => {});
  }, [customerId]);

  if (!overview || !products) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const chain = ["Meta / Google", "Campaign", "Lead", "Customer", "Staff", "Sale", "Payment", "Revenue"];

  return (
    <div className="space-y-6 animate-float-up" data-testid="attribution-panel">
      {/* Chain */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <h3 className="font-display font-semibold mb-4">Full-Funnel Attribution Chain</h3>
        <div className="flex flex-wrap items-center gap-2">
          {chain.map((c, i) => (
            <React.Fragment key={c}>
              <span className={`px-3 py-2 rounded-lg text-xs font-mono font-semibold ${i >= 5 ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>{c}</span>
              {i < chain.length - 1 && <ArrowRight className="w-4 h-4 text-muted-foreground/40" />}
            </React.Fragment>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {[
            { l: "First Touch", v: "Meta · IG Reels" },
            { l: "Assist Touches", v: "3 avg" },
            { l: "Last Touch", v: "WhatsApp CTA" },
            { l: "Sales Owner", v: "Nurul / Farah" },
          ].map((x) => (
            <div key={x.l} className="bg-secondary/30 rounded-xl p-4"><p className="text-xs text-muted-foreground">{x.l}</p><p className="text-sm font-medium mt-1">{x.v}</p></div>
          ))}
        </div>
      </div>

      {/* Product profitability */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <h3 className="font-display font-semibold mb-1">Product Profitability</h3>
        <p className="text-xs text-muted-foreground mb-4">AI favours real business economics (contribution) — not just higher revenue.</p>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-xs uppercase"><tr>{["Product", "Revenue", "Ad Spend", "Gross Margin", "Contribution", "C-ROAS"].map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.product} data-testid={`product-row-${p.product.replace(/\s/g, "-")}`} className="border-t border-border">
                  <td className="px-3 py-3 font-medium">{p.product}</td>
                  <td className="px-3 py-3 font-mono">{rm(p.revenue)}</td>
                  <td className="px-3 py-3 font-mono text-muted-foreground">{rm(p.adSpend)}</td>
                  <td className="px-3 py-3 font-mono">{rm(p.grossMargin)}</td>
                  <td className="px-3 py-3 font-mono text-accent font-semibold">{rm(p.contribution)}</td>
                  <td className="px-3 py-3 font-mono text-primary">{(p.contribution / p.adSpend).toFixed(1)}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
